import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useDebugger } from './DebuggerContext';
import { useTenant } from './TenantContext';
import Loader from '../components/common/loader';
import Saas404 from '../components/common/saas/Saas404';
import { runExternalAuthPipeline } from '../auth/externalAuthPipeline';
import { useExternalAuthChecks } from '../auth/useExternalAuthChecks';

type AuthenticationState =
  | 'initializing'
  | 'checking_ngw'
  | 'checking_external'
  | 'authenticated'
  | 'error';

interface AuthenticationCoordinatorProps {
  children: ReactNode;
  onStateChange?: (state: AuthenticationState) => void;
}

const AuthCoordinator: React.FC<AuthenticationCoordinatorProps> = ({
  children,
  onStateChange,
}) => {
  const {
    isLoading: isTenantConfigLoading,
    isAddEditHost,
    hasProvidedTenantName,
  } = useTenant();

  const [willNotLogin, setWillNotLogin] = useState(false);
  const [authState, setAuthState] =
    useState<AuthenticationState>('initializing');
  const [loadingMessage, setLoadingMessage] = useState(
    'Initializing authentication...',
  );
  const [error, setError] = useState<Error | null>(null);
  const hasInitialized = useRef(false);

  const { checkAuthentication } = useAuth();
  const externalAuthChecks = useExternalAuthChecks();
  const debug = useDebugger();

  const updateAuthState = useCallback(
    (newState: AuthenticationState) => {
      setAuthState(newState);
      onStateChange?.(newState);
    },
    [onStateChange],
  );

  useEffect(() => {
    if (hasInitialized.current || isTenantConfigLoading) {
      return;
    }

    // Check if we're on addedit host without a tenant name
    if (isAddEditHost() && !hasProvidedTenantName) {
      debug.log(
        'AuthCoordinator',
        'AddEdit host without tenant name - checking for redirect',
        'warn',
      );

      const timeoutId = setTimeout(() => {
        if (!hasInitialized.current) {
          debug.log(
            'AuthCoordinator',
            'No redirect occurred - showing 404',
            'warn',
          );
          setWillNotLogin(true);
          hasInitialized.current = true;
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    hasInitialized.current = true;

    const coordinateAuthentication = async () => {
      try {
        debug.group('AuthCoordinator', 'Starting authentication flow');

        // --- Step 1: NGW Authentication ---
        debug.log('AuthCoordinator', 'Checking NGW authentication');
        setLoadingMessage('Verifying credentials...');
        updateAuthState('checking_ngw');

        const ngwSession = await checkAuthentication();

        if (!ngwSession || !ngwSession.session) {
          if (ngwSession?.willNotLogin) {
            setWillNotLogin(true);
          }
          debug.log(
            'AuthCoordinator',
            'NGW authentication required, initiating redirect',
            'warn',
          );
          return;
        }

        debug.log('AuthCoordinator', 'NGW authentication successful');

        // --- Step 2: External Auth Pipeline ---
        const enabledChecks = externalAuthChecks.filter((c) => c.enabled);

        if (enabledChecks.length === 0) {
          debug.log(
            'AuthCoordinator',
            'No external auth checks enabled, completing authentication chain.',
          );
          updateAuthState('authenticated');
          return;
        }

        updateAuthState('checking_external');

        const allPassed = await runExternalAuthPipeline(externalAuthChecks, {
          onCheckStart: (check) => {
            setLoadingMessage(check.loadingMessage);
          },
          log: (source, message, level) => {
            debug.log(source, message, level);
          },
        });

        if (allPassed) {
          debug.log('AuthCoordinator', 'All external auth checks passed');
          updateAuthState('authenticated');
        }
        // If !allPassed, a redirect is in progress — nothing to do
      } catch (err) {
        debug.log(
          'AuthCoordinator',
          `Authentication error: ${err instanceof Error ? err.message : 'Unknown error'}`,
          'error',
        );
        setError(
          err instanceof Error ? err : new Error('Authentication failed'),
        );
        updateAuthState('error');
      } finally {
        debug.groupEnd();
      }
    };

    coordinateAuthentication();
  }, [
    isTenantConfigLoading,
    isAddEditHost,
    hasProvidedTenantName,
    checkAuthentication,
    externalAuthChecks,
    debug,
    updateAuthState,
  ]);

  if (willNotLogin) {
    return <Saas404 target={'AUTH'} />;
  }

  if (authState === 'error' && error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <h2 className="text-red-700 font-semibold">Authentication Error</h2>
        <p className="text-red-600">{error.message}</p>
      </div>
    );
  }

  if (authState !== 'authenticated') {
    return <Loader showHeaderFooter={false} text={loadingMessage} />;
  }

  return <>{children}</>;
};

export default AuthCoordinator;
